/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import can from 'can/util/util';
import BaseList from 'scripts/models/base/list';
import _ from 'lodash';

const queueProcessDelayBaseInterval = 400;

Component.extend({
    tag: 'animation-queue',
    viewModel: {
        define: {
            queue: {
                Type: BaseList,
                Value: BaseList,
            },
        },
    },
    events: {
        pendingDeferreds: [],
        pendingDeferredsResolveTimeout: [],

        inserted: function() {
            this.viewModel.attr('queue').bind('add', () => {
                this.rafHandler();
            });
        },

        rafHandler: function(queue) {
            window.requestAnimationFrame(() => {
                if (this.viewModel.attr('queue.length')) {
                    this.queueHandler();
                }
            });
        },

        queueHandler: function() {
            this.getPendingDeferreds(this.processIncomingQueueItems());
            this.clearPreviousTimeout();
            this.throttledResolve();
            this.debouncedQueueHandler();
        },

        processIncomingQueueItems: function() {
            can.batch.start();
            let queue = this.viewModel.attr('queue').toArray();
            this.viewModel.attr('queue').replace([]);
            can.batch.stop();
            return queue;
        },

        getPendingDeferreds: function(queue) {
            let allDeferreds = this.pendingDeferreds.concat(queue);

            this.pendingDeferreds = [];
            _.each(allDeferreds, (deferred) => {
                if (deferred.state() === 'pending') {
                    this.pendingDeferreds.push(deferred);
                }
            });
        },

        throttledResolve: _.throttle(function() {
            let shifted = this.pendingDeferreds.shift();
            if (!shifted) {
                return;
            }
            shifted.resolve();
        }, queueProcessDelayBaseInterval / 3),

        debouncedQueueHandler: _.debounce(function() {
            this.setResolveTimeout();
        }, queueProcessDelayBaseInterval),

        clearPreviousTimeout: function() {
            clearTimeout(this.pendingDeferredsResolveTimeout);
        },

        setResolveTimeout: function() {
            if (this.pendingDeferreds.length) {
                this.throttledResolve();
                this.pendingDeferredsResolveTimeout = setTimeout(() => {
                    this.setResolveTimeout();
                }, this.getResolveDuration());
            } else {
                this.clearPreviousTimeout();
            }
        },

        getResolveDuration: function() {
            return (
                queueProcessDelayBaseInterval /
                Math.max(1, this.pendingDeferreds.length / 3)
            );
        },
    },
});
